import { upgradeCanvasDocument, type CanvasEdge } from "./studio.js";
import { promptContentSchema, type PromptBinding, type PromptContent, type PromptSource, type ReferenceKind } from "./prompt-content.js";
import { canvasDocumentV3Schema, type CanvasDocumentV3, type CanvasNodeV3 } from "./studio-v3.js";
import { resolveTargetReferencesV3, type ReferenceBindingDiagnostic } from "./reference-core.js";

const kinds: Record<string, ReferenceKind> = { 图片: "image", 视频: "video", 音频: "audio" };
const compareEdges = (a: CanvasEdge, b: CanvasEdge): number => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
type MediaNode = Extract<CanvasNodeV3, { kind: ReferenceKind }>;
const isMedia = (node: CanvasNodeV3 | undefined): node is MediaNode => node?.kind === "image" || node?.kind === "video" || node?.kind === "audio";

/** One pass over the ORIGINAL string. Labels retain the exact legacy token. */
function convertPrompt(text: string): PromptContent {
  const segments: PromptContent["segments"] = [];
  let offset = 0;
  for (const match of text.matchAll(/@(图片|视频|音频)([1-9]\d*)/gu)) {
    if (match.index > offset) segments.push({ type: "text", text: text.slice(offset, match.index) });
    const kind = kinds[match[1]!]!;
    segments.push({ type: "reference", slotId: `legacy_${kind}_${match[2]}`, kind, label: match[0] });
    offset = match.index + match[0].length;
  }
  if (offset < text.length) segments.push({ type: "text", text: text.slice(offset) });
  // Reject unrepresentable/overlong tokens and segment counts, never truncate raw input.
  return promptContentSchema.parse({ segments });
}

function diagnosticsFor(document: CanvasDocumentV3): ReferenceBindingDiagnostic[] {
  return document.nodes.flatMap((node) => node.kind === "seedance"
    ? resolveTargetReferencesV3(document, node.id).diagnostics : []);
}

/** Lossless, browser-safe migration. Invalid structure/unknown versions throw without mutating input. */
export function upgradeCanvasDocumentV3(input: unknown): { document: CanvasDocumentV3; diagnostics: ReferenceBindingDiagnostic[] } {
  if (typeof input === "object" && input !== null && "schemaVersion" in input && input.schemaVersion === 3) {
    const document = canvasDocumentV3Schema.parse(input);
    return { document, diagnostics: diagnosticsFor(document) };
  }
  const legacy = upgradeCanvasDocument(input);
  const document: CanvasDocumentV3 = {
    ...legacy, schemaVersion: 3,
    nodes: legacy.nodes.map((node): CanvasNodeV3 => {
      if (node.kind === "text") return { ...node, text: convertPrompt(node.text) };
      if (node.kind === "script") return { ...node, title: convertPrompt(node.title), synopsis: convertPrompt(node.synopsis) };
      if (node.kind === "seedance") return { ...node, settings: { ...node.settings, prompt: convertPrompt(node.settings.prompt) }, referenceBindings: [] };
      return node;
    })
  };
  // V2 permitted dangling endpoints; V3 cannot safely persist them. Validate
  // structure before binding, leaving the original graph untouched on rejection.
  canvasDocumentV3Schema.parse(document);
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  for (const target of document.nodes) {
    if (target.kind !== "seedance") continue;
    const incoming = legacy.edges.filter((edge) => edge.targetNodeId === target.id);
    const media = incoming.flatMap((edge) => {
      const node = nodes.get(edge.sourceNodeId);
      return isMedia(node) ? [{ edge, node }] : [];
    });
    const codePoint = [...media].sort((a, b) => compareEdges(a.edge, b.edge));
    // Actual legacy editor AND compiler use localeCompare. The legacy visible
    // preview/reference map use code points; a disagreeing ordinal is ambiguous.
    const locale = [...media].sort((a, b) => a.edge.order - b.edge.order || a.edge.id.localeCompare(b.edge.id));
    const bind = (content: PromptContent, source: PromptSource): void => {
      const seen = new Set<string>();
      for (const segment of content.segments) {
        if (segment.type !== "reference" || seen.has(segment.slotId)) continue;
        seen.add(segment.slotId);
        const ordinal = Number(segment.slotId.slice(segment.slotId.lastIndexOf("_") + 1)) - 1;
        const old = locale.filter((item) => item.node.kind === segment.kind)[ordinal];
        const preview = codePoint.filter((item) => item.node.kind === segment.kind)[ordinal];
        const value: PromptBinding["value"] = old && preview?.edge.id === old.edge.id
          ? { state: "bound", target: { edgeId: old.edge.id, nodeId: old.node.id, assetId: old.node.assetId, kind: old.node.kind } }
          : { state: "missing", reason: "legacy_unresolved" };
        target.referenceBindings.push({ source, slotId: segment.slotId, value });
      }
    };
    bind(target.settings.prompt, { nodeId: target.id, field: "prompt", inputEdgeId: null });
    for (const edge of [...incoming].sort(compareEdges)) {
      const source = nodes.get(edge.sourceNodeId)!;
      if (source.kind === "text") bind(source.text, { nodeId: source.id, field: "text", inputEdgeId: edge.id });
      if (source.kind === "script") {
        bind(source.title, { nodeId: source.id, field: "title", inputEdgeId: edge.id });
        bind(source.synopsis, { nodeId: source.id, field: "synopsis", inputEdgeId: edge.id });
      }
    }
  }
  const validated = canvasDocumentV3Schema.parse(document);
  return { document: validated, diagnostics: diagnosticsFor(validated) };
}
